// src/stores/notificationSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { NotificationService } from '@/services';

// Async thunk to fetch the current unread notification count from the API
export const fetchNotificationCount = createAsyncThunk<
    number,         // return type of payload
    void            // argument type
>(
    'notifications/fetchCount',
    async () => {
        const response = await NotificationService.getNotificationCount();
        // assuming API returns { count: number }
        return response.data;
    }
);

// Async thunk to mark a notification as read and decrement the badge count
export const markAsRead = createAsyncThunk<
    void,
    number,         // notification ID
    { dispatch: any }
>(
    'notifications/markAsRead',
    async (id, { dispatch }) => {
        await NotificationService.markAsRead({ id });
        dispatch(decrementNotification());
    }
);

// The shape of our notifications slice state
interface NotificationState {
    count: number;
}

const initialState: NotificationState = {
    count: 0,
};

const notificationSlice = createSlice({
    name: 'notifications',
    initialState,
    reducers: {
        // Decrement the badge count by 1 (not going below zero)
        decrementNotification: (state) => {
            state.count = Math.max(state.count - 1, 0);
        },
        // Increment the badge count by 1 (for new incoming notifications)
        incrementNotification: (state) => {
            state.count += 1;
        },
        // Set the badge count to an exact value (from socket or API)
        setNotificationCount: (state, action: PayloadAction<number>) => {
            state.count = action.payload ?? 0;
        },
    },
    extraReducers: (builder) => {
        // When fetchNotificationCount completes, set the count
        builder.addCase(fetchNotificationCount.fulfilled, (state, action: PayloadAction<number>) => {
            state.count = action.payload;
        });
    },
});

// Export actions for dispatching
export const { decrementNotification, incrementNotification, setNotificationCount } = notificationSlice.actions;

// Export the reducer to include in the store
export default notificationSlice.reducer;
